// =============================================================================
// Header.js — Barre de navigation principale d'Aramis, extraite d'App.jsx
// (Révision S1 tranche 3). Déplacement pur : le texte des composants est
// repris à l'identique depuis App.jsx, seul l'export default a été ajouté.
// Contenu : Header. Les notifications (30/09/2026) : lecture et cloche par
// src/notificationsLecture.js et src/notificationsRegles.js, panneau paresseux
// src/CentreNotifications.js.
// Lot L6 (28/09/2026) : l'en-tête ne porte plus la navigation entre modules
// (rail, barre d'onglets et feuille « Modules », générés par buildNavModules
// dans src/navConstants.js) ; le menu de l'avatar et le tiroir « Menu » sont
// remplacés par « Moi » (src/FeuilleMoi.js).
// =============================================================================
import React, { useState, useEffect, useMemo, useRef, Suspense } from "react";
import { createPortal } from "react-dom";
import { ModuleTopStripe, AramisGlobalStyles, useConfirm } from "./uxComponents";
import { hasValidLegacyAdmin } from "./appShared";
import BoutonSynchroCowork from "./synchronisation/BoutonSynchroCowork";
import { CadenasAdmin } from "./cadenasAdmin";
import { ARGOS_VIEWS, MODULE_VIEWS, buildNavModules, estProprietaireBudget, filCourant } from "./navConstants";
import { Icone } from "./icones";
import { optsCoquille } from "./navigationCoquille";
import { lazyPrechargeable } from "./lazyRetry";
import { useCloche } from "./notificationsCloche";
import { echelleTexte } from "./echelleTexte";
import { peutAccederRH } from "./droitsRH";
// « Moi » (lot L6) : morceau à part, préchargé quand la page est au repos —
// il ne pèse pas sur le démarrage (budget du bundle principal).
const FeuilleMoi = lazyPrechargeable(() => import("./FeuilleMoi"));
// Centre de notifications (30/09/2026) : morceau à part, chargé à la première
// ouverture de la cloche et préchargé au repos, comme « Moi ».
const CentreNotifications = lazyPrechargeable(() => import("./CentreNotifications"));

// ── En-tête de la coquille (lot L6 « Coquille et transitions », 28/09/2026) ──
// Synthèse §6.1 à §6.3. L'en-tête ne porte PLUS la navigation entre modules :
// elle vit dans le rail (bureau, iPad en paysage) et dans la barre d'onglets
// avec sa feuille « Modules » (téléphone, iPad en portrait), toutes générées par
// buildNavModules() (src/navConstants.js). Disparaissent : les boutons de
// module et leurs panneaux survolés, le bouton « Menu » et son tiroir, le menu
// ⚙️ d'administration, la croix de déconnexion et le menu de l'avatar — leur
// contenu est repris par « Moi » (src/FeuilleMoi.js), sauf l'administration,
// qui vit depuis le 29/09/2026 dans le rail après Pilotage (buildNavAdmin).
//
// Il porte : la marque (retour à « Ma semaine »), le FIL COURANT « Enio /
// module / page » (au doigt : le titre de la page), la pilule « Rechercher ou
// agir » (⌘K / Ctrl K), Socrate juste après elle (11/10/2026, l'équipe
// seulement, une fois sa porte serveur ouverte), la synchronisation avec Cowork
// (super-administrateur, 14/10/2026), la messagerie (décision n° 8), les
// notifications et l'avatar, qui ouvre « Moi ». Opaque : plus de
// verre sur un élément collant.
const MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || "");

const Header = ({view,onNav,session,aramisSession,logoutAramis,
  impersonateAs,setImpersonateAs,delegates=[],onOpenGlobalSearch,ndfEligible=false,espaceSalarieEligible=false,
  moiOuvert=false,setMoiOuvert=null,messagesNonLus=0,onOuvrirMessagerie=null,
  socrateOuvert=false,onBasculerSocrate=null,isHabiliteHestia=false,hestiaAssistanteSeule=false,drapeauAudit=null,synchroCowork=false,isRhMaitre=false}) => {
  const confirm = useConfirm();
  const [showNotifs,setShowNotifs]=useState(false);
  const [showVoirComme,setShowVoirComme]=useState(false);
  const [voirCommePos,setVoirCommePos]=useState({top:0,left:0});
  const [voirCommeSearch,setVoirCommeSearch]=useState("");
  // « Moi » est piloté par App (l'onglet « Moi » l'ouvre aussi) ; à défaut
  // (aperçu, test), l'en-tête garde son propre état.
  const [moiLocal,setMoiLocal]=useState(false);
  const moiEstOuvert = setMoiOuvert ? moiOuvert : moiLocal;
  const ouvrirMoi = (v) => (setMoiOuvert || setMoiLocal)(v);
  const avatarRef = useRef(null);
  // « Moi » n'est monté qu'à la première ouverture (puis reste monté pour sa
  // sortie animée) ; son code est préchargé cinq secondes après l'arrivée.
  const [moiMonte, setMoiMonte] = useState(false);
  useEffect(() => { if (moiEstOuvert) setMoiMonte(true); }, [moiEstOuvert]);
  const idSession = aramisSession ? aramisSession.id : null;
  useEffect(() => {
    if (!idSession) return undefined;
    const t = setTimeout(() => { FeuilleMoi.preload(); CentreNotifications.preload(); }, 5000);
    return () => clearTimeout(t);
  }, [idSession]);

  const MA_SEMAINE_VIEWS=MODULE_VIEWS.maSemaine;
  const isMaSemaine=MA_SEMAINE_VIEWS.includes(view);
  const isAthos=MODULE_VIEWS.athos.includes(view);
  // Affaires publiques : module à part depuis le 12/10/2026 (indigo).
  const isAP=MODULE_VIEWS.ap.includes(view);
  const isExploration=MODULE_VIEWS.exploration.includes(view);
  const isArgos=ARGOS_VIEWS.includes(view);
  const isMutuelles=MODULE_VIEWS.mutuelles.includes(view);
  const isGouv=MODULE_VIEWS.gouv.includes(view);
  const isEvents=MODULE_VIEWS.events.includes(view);
  const isRH=MODULE_VIEWS.rh.includes(view);
  const isHygie=MODULE_VIEWS.hygie.includes(view);
  // Module "Conformité" (Étape 2 v2) = Calypso/RGPD + Gel PPE. La veille
  // juridique a rejoint les Affaires publiques le 18/10/2026 (MODULE_VIEWS.ap).
  const isConformite = isHygie || view==="gel-avoirs-ppe" || view==="accueil-conformite";
  const isPilotage = MODULE_VIEWS.pilotage.includes(view); // source unique : navConstants
  // isAdmin réel (pour les contrôles admin : Voir comme, etc.)
  const isAdminReal=!!(aramisSession?.role==="admin"||aramisSession?.role==="superadmin"||hasValidLegacyAdmin());

  // En mode impersonation, reflète les droits de la personne impersonée.
  const isAdmin = impersonateAs
    ? !!(impersonateAs.role==="admin"||impersonateAs.role==="superadmin")
    : isAdminReal;
  // Le fil courant se calcule sur le menu RÉELLEMENT visible par la personne
  // AFFICHÉE (« Voir comme ») : mêmes règles et même session que le menu
  // d'App.jsx (rail, onglets), sinon le fil et le rail divergent.
  const sessionNav = impersonateAs || aramisSession;
  // Accès au module Gel des avoirs & PPE (LCB-FT) : même règle qu'avant.
  const canAccessGelPpe = (() => {
    if (!sessionNav) return false;
    if (isAdmin) return true;
    const roles = Array.isArray(sessionNav.roles) ? sessionNav.roles : [];
    if (roles.includes("DG") || roles.includes("CD")) return true;
    const polesPilotage = new Set([
      "actuariat et pilotage de la donnee",
      "conformite juridique et protection des donnees",
      "gestion des risques controle interne et audit interne",
      "administration comptabilite finance",
    ]);
    const normPole = s => String(s||"")
      .normalize("NFD").replace(/[̀-ͯ]/g,"")
      .toLowerCase().replace(/[^a-z0-9]+/g," ").trim();
    return (sessionNav.polesInteret||[]).some(p => polesPilotage.has(normPole(p)));
  })();
  const canAccessHygie = (() => {
    if (!sessionNav) return false;
    if (isAdmin) return true;
    const me = delegates.find(d => d.id === sessionNav.id) || sessionNav;
    const roles = me.roles || [];
    return roles.includes("DPO") || roles.includes("DPO_ADJOINT");
  })();
  const canAccessRHnav = (() => {
    if (!sessionNav) return false;
    // Fiche à jour plutôt que la session : un retrait d'habilitation doit
    // fermer le module sans attendre une reconnexion.
    const me = delegates.find(d => d.id === sessionNav.id) || sessionNav;
    return peutAccederRH(me, { isAdmin });
  })();
  // Notifications : session RÉELLE (jamais « Voir comme »), pôles et adresse
  // lus sur la fiche à jour, projets suivis pour les mises à jour de projet.
  const maFiche = aramisSession ? delegates.find(d => d.id === aramisSession.id) : null;
  const moiNotifs = useMemo(() => (aramisSession ? { ...aramisSession,
    email: (maFiche && maFiche.email) || aramisSession.email,
    polesInteret: (maFiche && maFiche.polesInteret) || aramisSession.polesInteret } : null), [aramisSession, maFiche]);
  const favoris = maFiche ? (Array.isArray(maFiche.favorisProjets) ? maFiche.favorisProjets : []) : null;
  const centre = useCloche(moiNotifs, isAdminReal, favoris);
  const [notifsMonte, setNotifsMonte] = useState(false);
  useEffect(() => { if (showNotifs) setNotifsMonte(true); }, [showNotifs]);
  const isSessionLive = !!session;

  const headerRef = useRef(null);
  // Au doigt, le titre de la page n'apparaît dans l'en-tête qu'une fois le
  // haut de la page passé sous lui (repère posé par App : [data-cq-sentinelle]).
  const [titreVisible, setTitreVisible] = useState(false);
  useEffect(() => {
    const repere = document.querySelector("[data-cq-sentinelle]");
    if (!repere || typeof IntersectionObserver === "undefined") { setTitreVisible(false); return undefined; }
    const io = new IntersectionObserver(([e]) => setTitreVisible(!e.isIntersecting));
    io.observe(repere);
    return () => io.disconnect();
  }, [view]);

  // Expose la hauteur RÉELLE de l'en-tête dans `--app-header-h` (ResizeObserver
  // + resize) : les fenêtres, la barre de progression, les feuilles et le rail
  // s'ouvrent SOUS lui. Fix récurrent iPad : les modales étaient rognées.
  useEffect(() => {
    const setVar = () => {
      const h = headerRef.current ? headerRef.current.offsetHeight : 56;
      document.documentElement.style.setProperty("--app-header-h", h + "px");
    };
    setVar();
    let ro = null;
    if (typeof ResizeObserver !== "undefined" && headerRef.current) {
      ro = new ResizeObserver(setVar);
      ro.observe(headerRef.current);
    }
    window.addEventListener("resize", setVar);
    window.addEventListener("orientationchange", setVar);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", setVar);
      window.removeEventListener("orientationchange", setVar);
    };
  }, []);

  // Menu réellement visible par la personne : sert au fil courant.
  const NAV_MODULES = buildNavModules({ isAdmin, canAccessGelPpe, canAccessHygie, canAccessRHnav, isSessionLive, audit: drapeauAudit, isRhMaitre, isProprietaireBudget: estProprietaireBudget(sessionNav), isHabiliteHestia, hestiaAssistanteSeule });
  const fil = aramisSession ? filCourant(view, NAV_MODULES, { admin: !!isAdmin }) : [];
  const titrePage = fil.length ? fil[fil.length - 1].libelle : "Aramis";

  // Placement du pop-up « Voir comme » (largeur 300, coords body) sous l'avatar.
  // Zoom-aware : on calcule en coords viewport puis on divise par le zoom du
  // body (réglage de taille du texte), sinon le pop-up part hors écran. Sa
  // largeur ne dépasse jamais l'écran moins deux marges (Très grand sur un
  // iPhone de 390 px : 414 px rendus, bord droit coupé) : même borne en CSS.
  const computeVoirCommePos = (btnEl) => {
    const r = btnEl.getBoundingClientRect();
    const zoom = echelleTexte();
    const margin = 8;
    const wView = Math.min(300 * zoom, window.innerWidth - 2 * margin);
    const hView = 300 * zoom;
    const vw = window.innerWidth, vh = window.innerHeight;
    let leftView = r.right - wView;
    if (leftView + wView > vw - margin) leftView = vw - margin - wView;
    if (leftView < margin) leftView = margin;
    let topView = r.bottom + 4 * zoom;
    if (topView + hView > vh - margin) topView = Math.max(margin, vh - margin - hView);
    return { top: topView / zoom, left: leftView / zoom };
  };

  // Liseré de module (ModuleTopStripe) : teinte du module de la vue.
  const currentModule = isMaSemaine ? "cockpit"
    : isEvents ? "events"
    : isGouv ? "gouv"
    : isAthos  ? "athos"
    : isAP ? "ap"
    : isMutuelles ? "mutuelles"
    : isExploration ? "exploration"
    : isArgos ? "argos"
    : isConformite ? "hygie"
    : isRH ? "rh"
    : isPilotage ? "pilotage"
    : view === "admin" ? "admin"
    : "default";

  const initiales = aramisSession ? ((aramisSession.prenom||"").charAt(0)+(aramisSession.nom||"").charAt(0)) : "";
  const deconnecter = async () => {
    if (await confirm({ title:"Se déconnecter ?", message:"Vous allez quitter votre session Aramis.", confirmLabel:"Se déconnecter", danger:true })) logoutAramis();
  };

  return(
  <>
  <AramisGlobalStyles/>
  <header ref={headerRef} className="app-header cq-entete" data-titre={titreVisible ? "1" : undefined}>
    {/* Marque Enio : retour à « Ma semaine » (si connecté) ou à la page de garde */}
    <button type="button" className="cq-entete-marque"
      title={aramisSession?"Ma semaine":"Accueil"} aria-label={aramisSession?"Aramis : Ma semaine":"Aramis : accueil"}
      onClick={(e)=>onNav(aramisSession?"ma-semaine":"page-garde", null, optsCoquille(e))}>
      <img src="/enio-logo-blanc.png" alt="" className="header-logo-dark"/>
      <img src="/enio-logo-bleu.png" alt="" className="header-logo-light"/>
    </button>
    {aramisSession && (
      <nav className="cq-fil" aria-label="Vous êtes ici">
        <span className="cq-fil-racine">Enio</span>
        {fil.map((e, i) => (
          <React.Fragment key={i + e.libelle}>
            <span className="cq-fil-sep" aria-hidden="true">/</span>
            {i < fil.length - 1 && e.route
              ? <button type="button" className="cq-fil-lien" onClick={(ev)=>onNav(e.route, null, optsCoquille(ev))}>{e.libelle}</button>
              : <span className="cq-fil-courant" aria-current={i === fil.length - 1 ? "page" : undefined}>{e.libelle}{e.reserve && <CadenasAdmin />}</span>}
          </React.Fragment>
        ))}
      </nav>
    )}
    {/* Au doigt, le titre de la page remplace le fil (coquilleStyles.js). */}
    {aramisSession && <p className="cq-entete-titre" aria-hidden="true">{titrePage}{fil.length > 0 && fil[fil.length - 1].reserve && <CadenasAdmin />}</p>}
    <div className="cq-entete-actions">
      {/* Session invitée : l'invité sait pourquoi aucune action ne lui est proposée. */}
      {aramisSession?.role === "invite" && (
        <span className="cq-badge-invite" title="Compte invité : vous découvrez Aramis en consultation seule, aucune action ni modification possible.">
          <Icone name="eye" size={14}/><span>Invité · lecture seule</span>
        </span>
      )}
      {/* « Voir comme X » : l'indicateur reste visible tant que l'usurpation dure. */}
      {aramisSession && isAdminReal && impersonateAs && setImpersonateAs && (
        <button type="button" className="cq-voir-comme" onClick={()=>setImpersonateAs(null)}
          title={"Quitter la vue de "+impersonateAs.prenom+" "+impersonateAs.nom}>
          <Icone name="eye" size={14}/>
          <span>{impersonateAs.prenom} {(impersonateAs.nom||"")[0]}.</span>
          <Icone name="x" size={12}/>
        </button>
      )}
      {aramisSession && onOpenGlobalSearch && (
        <button type="button" id="header-search" className="cq-recherche" onClick={onOpenGlobalSearch}
          aria-label={"Rechercher ou agir (" + (MAC ? "⌘ K" : "Ctrl K") + ")"}>
          <Icone name="search" size={18}/>
          <span className="cq-recherche-texte">Rechercher ou agir</span>
          <span className="cq-kbd" aria-hidden="true"><kbd>{MAC ? "⌘" : "Ctrl"}</kbd><kbd>K</kbd></span>
        </button>
      )}
      {/* Socrate : son « S » reste son identité (tête de son panneau). Le
          bouton ne paraît qu'une fois la porte serveur ouverte (App le sait
          par le composant) ; au doigt, il passe par « Moi », comme la
          messagerie (coquilleStyles.js). */}
      {aramisSession && onBasculerSocrate && (
        <button type="button" className="cq-icone-bouton cq-socrate" onClick={onBasculerSocrate}
          aria-haspopup="dialog" aria-expanded={socrateOuvert} aria-controls={socrateOuvert ? "cq-socrate" : undefined}
          aria-label="Socrate, interlocuteur de l'équipe" title="Socrate">
          <span className="cq-socrate-s" aria-hidden="true">S</span>
        </button>
      )}
      {/* Synchronisation avec Cowork (14/10/2026) : super-administrateur strict
          de la session réelle, décidé par App ; au doigt, la page Synchronisation. */}
      {aramisSession && synchroCowork && <BoutonSynchroCowork session={aramisSession} onNav={onNav} />}
      {aramisSession && onOuvrirMessagerie && (
        <button type="button" className="cq-icone-bouton cq-messagerie" onClick={onOuvrirMessagerie}
          aria-label={"Messagerie" + (messagesNonLus > 0 ? ", " + messagesNonLus + " non lu" + (messagesNonLus > 1 ? "s" : "") : "")}>
          <Icone name="messages" size={20}/>
          {messagesNonLus > 0 && <span className="cq-compteur" aria-hidden="true">{messagesNonLus > 99 ? "99+" : messagesNonLus}</span>}
        </button>
      )}
      {aramisSession && (
        <>
          <button type="button" className="cq-icone-bouton" onClick={() => setShowNotifs(s => !s)}
            aria-expanded={showNotifs} aria-haspopup="dialog" aria-controls={showNotifs ? "cq-notifs" : undefined}
            aria-label={centre.libelle}>
            <Icone name="bell" size={20}/>
            {/* Le chiffre promet un acte (exact jusqu'à 99) ; une information non lue n'allume qu'un point. */}
            {centre.aTraiter > 0
              ? <span className="cq-compteur" aria-hidden="true">{centre.aTraiter > 99 ? "99+" : centre.aTraiter}</span>
              : centre.nouvelles > 0 && <span className="cq-compteur cq-compteur-point" aria-hidden="true"/>}
          </button>
          {/* Annonce ce qui SONNERAIT (à traiter, me nomme, arrivée seule) ; la
              clé remonte le texte, pour qu'une annonce identique soit relue. */}
          <span className="squelette-sr" aria-live="polite"><span key={centre.annonceN}>{centre.annonce}</span></span>
          {(notifsMonte || showNotifs) && (
            <Suspense fallback={null}>
              <CentreNotifications ouvert={showNotifs} onFermer={() => setShowNotifs(false)}
                notifs={centre.notifs} files={centre.files} moi={moiNotifs}
                pret={centre.pret} erreur={centre.erreur} onNav={onNav}
                marquerLues={centre.marquerLues} toutMarquerLu={centre.toutMarquerLu} marquerTraitees={centre.marquerTraitees} rouvrir={centre.rouvrir}/>
            </Suspense>
          )}
        </>
      )}
      {aramisSession ? (
        <button type="button" id="header-user-menu-btn" ref={avatarRef} className="cq-avatar-bouton"
          aria-haspopup="dialog" aria-expanded={moiEstOuvert}
          aria-label={"Moi : " + [aramisSession.prenom, aramisSession.nom].filter(Boolean).join(" ")}
          onClick={() => ouvrirMoi(true)}>
          <span className="cq-avatar" aria-hidden="true">{initiales || <Icone name="user" size={18}/>}</span>
        </button>
      ) : (
        <button type="button" className={"ptab-btn"+(view==="aramis-login"?" act":"")}
          onClick={()=>onNav("aramis-login")}>
          Connexion
        </button>
      )}
    </div>
  </header>
  <ModuleTopStripe module={currentModule}/>

  {aramisSession && (moiMonte || moiEstOuvert) && (
    <Suspense fallback={null}>
    <FeuilleMoi ouvert={moiEstOuvert} onFermer={() => ouvrirMoi(false)}
      session={aramisSession} onNav={onNav} view={view}
      ndfEligible={ndfEligible} espaceSalarieEligible={espaceSalarieEligible}
      messagesNonLus={messagesNonLus} onMessagerie={onOuvrirMessagerie}
      onSocrate={onBasculerSocrate ? () => { if (!socrateOuvert) onBasculerSocrate(); } : null}
      isAdmin={isAdminReal}
      impersonateAs={impersonateAs}
      onQuitterVoirComme={setImpersonateAs ? () => setImpersonateAs(null) : null}
      onVoirComme={setImpersonateAs ? () => {
        if (avatarRef.current) setVoirCommePos(computeVoirCommePos(avatarRef.current));
        setVoirCommeSearch("");
        setShowVoirComme(true);
      } : null}
      onDeconnexion={deconnecter}/>
    </Suspense>
  )}

  {/* Dropdown "Voir comme" — portail document.body (bloc contenant du header). */}
  {showVoirComme && createPortal(
    <>
      {/* Règle 24/08 : la fermeture au clic extérieur est conservée (menu de
          navigation) mais ne JETTE plus la recherche saisie — elle est
          réinitialisée à la réouverture du menu. */}
      <div style={{position:"fixed",inset:0,zIndex:9999}} onClick={()=>setShowVoirComme(false)}/>
      <div style={{position:"fixed",top:voirCommePos.top,left:voirCommePos.left,
        background:"var(--bg-card)",
        border:"1px solid var(--border-color)",
        borderRadius:"var(--radius-md)",padding:"12px",zIndex:10000,width:"min(300px, calc(var(--vw100) - 16px / var(--fs-zoom, 1)))",
        boxShadow:"var(--elev-2, var(--shadow-elevated))"}}>
        <div style={{fontSize:"var(--fs-micro)",color:"var(--text-sub)",marginBottom:10,lineHeight:1.4}}>
          Choisissez l'identité du compte dont vous voulez visualiser l'affichage dans Aramis.
        </div>
        <input className="inp" placeholder="Rechercher un membre..."
          autoFocus value={voirCommeSearch}
          onChange={e=>setVoirCommeSearch(e.target.value)}
          style={{marginBottom:6}}/>
        {voirCommeSearch.length>=2 && (
          <div style={{maxHeight:200,overflowY:"auto"}}>
            {delegates.filter(d=>d.actif!==false && d.id!==aramisSession?.id &&
              ((d.prenom||"")+" "+(d.nom||"")).toLowerCase().includes(voirCommeSearch.toLowerCase())
            ).sort((a,b)=>(a.nom||"").localeCompare(b.nom||"","fr"))
            .slice(0,15).map(d=>(
              <button type="button" key={d.id} className="cq-voir-comme-choix"
                onClick={()=>{setImpersonateAs(d);setShowVoirComme(false);setVoirCommeSearch("");}}>
                <strong>{d.prenom} {d.nom}</strong>
                <span>{d.mutuelle}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </>,
    document.body
  )}
  </>
  );
};

export default Header;
